import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { 
  Add as AddIcon, 
  LocationOn as LocationIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { 
  getAllLocations, 
  createLocation, 
  updateLocation, 
  deleteLocation, 
  BusinessLocation,
  initializeDefaultLocations
} from '../../../firebase/services/locationService';
import LocationForm from '../../../components/admin/LocationForm';

const LocationsPage = () => {
  // State variables
  const [locations, setLocations] = useState<BusinessLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [locationToEdit, setLocationToEdit] = useState<BusinessLocation | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<BusinessLocation | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);

  // Initialize and load data
  useEffect(() => {
    loadLocations();
  }, []);

  // Load locations from Firestore
  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all locations
      const locationsData = await getAllLocations();
      setLocations(locationsData);
    } catch (err) {
      console.error('Error loading locations:', err);
      setError('Failed to load business locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Open menu for location actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, locationId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveLocationId(locationId);
  };

  // Close menu
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveLocationId(null);
  };

  // Open form to add a new location
  const handleAddLocation = () => {
    setLocationToEdit(undefined);
    setFormOpen(true);
  };

  // Open form to edit a location
  const handleEditLocation = () => {
    const locationToEdit = locations.find(location => location.id === activeLocationId);
    if (locationToEdit) {
      setLocationToEdit(locationToEdit);
      setFormOpen(true);
    }
    handleMenuClose();
  };

  // Open confirmation dialog to delete a location
  const handleDeleteClick = () => {
    const location = locations.find(location => location.id === activeLocationId);
    if (location) {
      setLocationToDelete(location);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  // Cancel deletion
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLocationToDelete(null);
  };

  // Confirm and execute deletion
  const handleDeleteConfirm = async () => {
    if (locationToDelete && locationToDelete.id) {
      try {
        setLoading(true);
        await deleteLocation(locationToDelete.id);
        // Refresh locations after deletion
        await loadLocations();
        setDeleteDialogOpen(false);
        setLocationToDelete(null);
      } catch (err) {
        console.error('Error deleting location:', err);
        setError('Failed to delete location. Please try again.');
        setLoading(false);
      }
    }
  };

  // Save a new or updated location
  const handleSaveLocation = async (locationData: Omit<BusinessLocation, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setLoading(true);
      
      if (locationToEdit && locationToEdit.id) {
        // Update existing location
        await updateLocation(locationToEdit.id, locationData);
      } else {
        // Create new location
        await createLocation(locationData);
      }
      
      // Refresh locations
      await loadLocations();
      setFormOpen(false);
    } catch (err) {
      console.error('Error saving location:', err);
      setError('Failed to save location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Display loading state
  if (loading && locations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Business Locations
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleAddLocation}
        >
          Add Location
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={3}>
        {locations.map((location) => (
          <Grid
            key={location.id}
            item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardHeader 
                title={location.name} 
                subheader={location.type}
                avatar={<LocationIcon color="primary" />}
                action={
                  <IconButton 
                    aria-label="settings" 
                    onClick={(e) => handleMenuOpen(e, location.id || '')}
                  >
                    <MoreVertIcon />
                  </IconButton>
                }
              />
              <Divider />
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {location.address}, {location.city}, {location.state} {location.zipCode}
                </Typography>
                
                {location.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {location.description}
                  </Typography>
                )}
                
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {location.primaryLocation && (
                    <Chip 
                      label="Primary" 
                      color="primary" 
                      size="small" 
                      sx={{ mr: 1 }}
                    />
                  )}
                  
                  <Chip 
                    label={location.active ? 'Active' : 'Inactive'} 
                    color={location.active ? 'success' : 'error'} 
                    size="small" 
                  />
                </Box>
                
                {location.phone && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Phone: {location.phone}
                  </Typography>
                )}
                
                {location.email && (
                  <Typography variant="body2" color="text.secondary">
                    Email: {location.email}
                  </Typography>
                )}
                
                {location.manager && (
                  <Typography variant="body2" color="text.secondary">
                    Manager: {location.manager}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {locations.length === 0 && !loading && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No locations found. Click "Add Location" to create one.
          </Typography>
        </Paper>
      )}
      {/* Location actions menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditLocation}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
      {/* Location form dialog */}
      <LocationForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleSaveLocation}
        initialData={locationToEdit}
        title={locationToEdit ? 'Edit Location' : 'Add New Location'}
      />
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Location</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {locationToDelete?.name}? This action cannot be undone.
            {locationToDelete?.primaryLocation && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">
                  Warning: This is your primary location. Deleting it may affect system functionality.
                </Alert>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationsPage; 